import { useState } from 'react'
import { PlugZap } from 'lucide-react'
import { Button, Chip, Input, Select, Toggle, toast } from '@/components/ui'
import {
  useCarriers,
  useTestCarrier,
  useUpdateCarrier,
  type CarrierCard,
  type CarrierMode,
} from '../api/carriers'

const MODE_LABEL: Record<CarrierMode, string> = {
  live: 'Live',
  dormant: 'Dormant (no API calls)',
  mock: 'Mock (deterministic fakes)',
}

/**
 * Settings → Freight Carriers.
 *
 * Cards render generically from each registered provider's self-described
 * metadata — when a new carrier adapter lands in the middleware registry,
 * its card appears here with zero frontend changes. Secrets are write-only:
 * stored values are never fetched; a configured field shows a placeholder
 * and submitting blank keeps the stored value.
 */
export function CarriersSection() {
  const { data, isLoading } = useCarriers()

  return (
    <section className="flex flex-col gap-4">
      <header>
        <h2 className="text-lg font-semibold text-primary">Freight Carriers</h2>
        <p className="text-sm text-secondary">
          SPI Freight provider configuration. A provider whose config still
          comes from env vars shows source <code>env</code>; saving here
          moves it to the database, which takes precedence.
        </p>
      </header>

      {isLoading ? (
        <div className="py-6 text-center text-sm text-tertiary">Loading…</div>
      ) : (
        (data?.carriers ?? []).map((carrier) => (
          <CarrierConfigCard key={carrier.provider} carrier={carrier} />
        ))
      )}
    </section>
  )
}

function CarrierConfigCard({ carrier }: { carrier: CarrierCard }) {
  const update = useUpdateCarrier()
  const test = useTestCarrier()

  const [enabled, setEnabled] = useState(carrier.enabled)
  const [mode, setMode] = useState<CarrierMode>(carrier.mode)
  const [apiBase, setApiBase] = useState(carrier.api_base)
  const [credentials, setCredentials] = useState<Record<string, string>>({})

  function save() {
    const dirtyCredentials = Object.fromEntries(
      Object.entries(credentials).filter(([, v]) => v !== ''),
    )
    update.mutate(
      {
        provider: carrier.provider,
        enabled,
        mode,
        api_base: apiBase,
        ...(Object.keys(dirtyCredentials).length > 0
          ? { credentials: dirtyCredentials }
          : {}),
      },
      {
        onSuccess: () => {
          setCredentials({})
          toast.success(`${carrier.display_name} configuration saved`)
        },
        onError: (err) =>
          toast.danger(
            `Save failed${err instanceof Error ? `: ${err.message}` : ''}`,
          ),
      },
    )
  }

  function testConnection() {
    test.mutate(carrier.provider, {
      onSuccess: (health) => {
        if (health.reachable) {
          toast.success(`${carrier.display_name}: connection OK`)
        } else {
          toast.danger(
            `${carrier.display_name}: unreachable${health.error ? ` — ${health.error}` : ''}`,
          )
        }
      },
      onError: (err) =>
        toast.danger(
          `Test failed${err instanceof Error ? `: ${err.message}` : ''}`,
        ),
    })
  }

  return (
    <section className="flex flex-col gap-3 rounded-sm border border-border-subtle bg-surface p-4">
      <header className="flex items-center gap-2">
        <h3 className="text-sm font-semibold text-primary">
          {carrier.display_name}
        </h3>
        <code className="text-role-metadata">{carrier.provider}</code>
        <Chip size="xs" variant={carrier.source === 'db' ? 'info' : 'neutral'}>
          {carrier.source === 'db' ? 'database' : 'env fallback'}
        </Chip>
        <span className="ml-auto flex gap-1">
          {carrier.capabilities.map((cap) => (
            <Chip key={cap} size="xs" variant="neutral">
              {cap}
            </Chip>
          ))}
        </span>
      </header>

      <div className="flex items-center gap-6">
        <Toggle
          label="Enabled"
          checked={enabled}
          onChange={(e) => setEnabled(e.target.checked)}
        />
        <label className="flex items-center gap-2 text-sm">
          <span className="text-secondary">Mode</span>
          <Select
            value={mode}
            onChange={(e) => setMode(e.target.value as CarrierMode)}
            aria-label={`${carrier.display_name} mode`}
          >
            {(Object.keys(MODE_LABEL) as CarrierMode[]).map((m) => (
              <option key={m} value={m}>
                {MODE_LABEL[m]}
              </option>
            ))}
          </Select>
        </label>
      </div>

      <label className="flex flex-col gap-1 text-sm">
        <span className="text-secondary">API base URL</span>
        <Input
          value={apiBase}
          onChange={(e) => setApiBase(e.target.value)}
          placeholder={carrier.default_api_base}
        />
      </label>

      <div className="grid grid-cols-2 gap-3">
        {carrier.credential_fields.map((field) => {
          const configured = carrier.credentials_set.includes(field.name)
          return (
            <label key={field.name} className="flex flex-col gap-1 text-sm">
              <span className="text-secondary">
                {field.label}
                {configured ? (
                  <span className="ml-1 text-role-metadata">(configured)</span>
                ) : null}
              </span>
              <Input
                type={field.secret ? 'password' : 'text'}
                autoComplete="off"
                value={credentials[field.name] ?? ''}
                onChange={(e) =>
                  setCredentials((prev) => ({
                    ...prev,
                    [field.name]: e.target.value,
                  }))
                }
                placeholder={
                  configured ? '•••••• (leave blank to keep)' : 'Not set'
                }
              />
            </label>
          )
        })}
      </div>

      <footer className="flex items-center justify-end gap-2">
        <Button
          variant="secondary"
          size="sm"
          loading={test.isPending}
          onClick={testConnection}
        >
          <PlugZap size={12} /> Test connection
        </Button>
        <Button
          variant="primary"
          size="sm"
          loading={update.isPending}
          onClick={save}
        >
          Save
        </Button>
      </footer>
    </section>
  )
}
